import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { getMainCategory } from '../../constants/categoryMapping';

import CategorySection from './components/CategorySection';
import FilterSection from './components/FilterSection';

const BrowseCategoriesAllVendorTypes = () => {
  const navigate = useNavigate();

  const handleCategoryClick = (categorySlug) => {
    // Convert subcategory slug to main database category
    const mainCategory = getMainCategory(categorySlug);
    navigate(`/category-browse?category=${mainCategory}&subcategory=${categorySlug}`);
  };

  // Entertainment categories
  const entertainmentCategories = [
    {
      id: 'dj-services',
      title: 'DJ Services',
      description: 'Professional DJs specializing in Bollywood, Bhangra, and fusion music',
      icon: 'Music',
      slug: 'dj-services'
    },
    {
      id: 'dhol-bhangra',
      title: 'Dhol Players & Bhangra Teams',
      description: 'Traditional dhol players and energetic bhangra performers',
      icon: 'Drum',
      slug: 'dhol-bhangra'
    },
    {
      id: 'mc-hosts',
      title: 'MCs & Hosts',
      description: 'Bilingual wedding hosts and master of ceremonies',
      icon: 'Mic',
      slug: 'mc-hosts'
    },
    {
      id: 'live-bands',
      title: 'Live Bands / Singers',
      description: 'Live musical performances and traditional singers',
      icon: 'Users',
      slug: 'live-bands'
    },
    {
      id: 'folk-dancers',
      title: 'Folk Dancers / Cultural Performers',
      description: 'Traditional dance performances and cultural entertainment',
      icon: 'Activity',
      slug: 'folk-dancers'
    }
  ];

  // Photography & Videography categories
  const photographyCategories = [
    {
      id: 'wedding-photographers',
      title: 'Wedding Photographers',
      description: 'Professional wedding photographers capturing your special moments',
      icon: 'Camera',
      slug: 'wedding-photographers'
    },
    {
      id: 'videographers',
      title: 'Cinematic Videographers',
      description: 'Cinematic wedding videography and storytelling',
      icon: 'Video',
      slug: 'videographers'
    },
    {
      id: 'photo-booth',
      title: 'Photo Booth Providers',
      description: 'Interactive photo booth experiences for guests',
      icon: 'Image',
      slug: 'photo-booth'
    },
    {
      id: 'drone-coverage',
      title: 'Drone Coverage',
      description: 'Aerial photography and videography services',
      icon: 'Plane',
      slug: 'drone-coverage'
    }
  ];

  // Beauty & Grooming categories
  const beautyCategories = [
    {
      id: 'bridal-makeup',
      title: 'Bridal Makeup Artists',
      description: 'Professional bridal makeup for South Asian weddings',
      icon: 'Palette',
      slug: 'bridal-makeup'
    },
    {
      id: 'hair-stylists',
      title: 'Bridal Hair Stylists',
      description: 'Traditional and modern bridal hair styling',
      icon: 'Scissors',
      slug: 'hair-stylists'
    },
    {
      id: 'mehndi-artists',
      title: 'Mehndi / Henna Artists',
      description: 'Beautiful henna designs for brides and guests',
      icon: 'Brush',
      slug: 'mehndi-artists'
    },
    {
      id: 'groom-services',
      title: 'Groom Services (Facials, Hair, etc.)',
      description: 'Grooming and styling services for grooms',
      icon: 'User',
      slug: 'groom-services'
    }
  ];

  // Cultural & Ritual Services categories
  const culturalCategories = [
    {
      id: 'turban-tying',
      title: 'Turban / Safa Tying',
      description: 'Traditional turban and safa tying services',
      icon: 'Crown',
      slug: 'turban-tying'
    },
    {
      id: 'ritual-helpers',
      title: 'Wedding Ritual Helpers',
      description: 'Assistance with traditional wedding ceremonies',
      icon: 'HandHeart',
      slug: 'ritual-helpers'
    },
    {
      id: 'pooja-services',
      title: 'Pooja / Granthi / Pandit Assistance',
      description: 'Religious ceremony officiants and assistance',
      icon: 'Flame',
      slug: 'pooja-services'
    },
    {
      id: 'attire-dressing',
      title: 'Cultural Attire Dressing Help',
      description: 'Professional assistance with traditional attire',
      icon: 'Shirt',
      slug: 'attire-dressing'
    }
  ];

  // Venues & Catering categories
  const venueCategories = [
    {
      id: 'banquet-halls',
      title: 'Banquet Halls',
      description: 'Traditional and modern wedding venue spaces',
      icon: 'Building',
      slug: 'banquet-halls'
    },
    {
      id: 'destination-venues',
      title: 'Destination / Outdoor Venues',
      description: 'Unique outdoor and destination wedding locations',
      icon: 'MapPin',
      slug: 'destination-venues'
    },
    {
      id: 'caterers',
      title: 'Caterers (South Asian, North Indian, South Indian, Halal, Veg, Gluten-free, Vegan)',
      description: 'Authentic cuisine catering with dietary accommodations',
      icon: 'ChefHat',
      slug: 'caterers'
    },
    {
      id: 'sweet-shops',
      title: 'Sweet Shops / Dessert Vendors',
      description: 'Traditional sweets and dessert specialists',
      icon: 'Cake',
      slug: 'sweet-shops'
    }
  ];

  // Decor & Production categories
  const decorCategories = [
    {
      id: 'event-decor',
      title: 'Event Decor / Floral Designers',
      description: 'Beautiful floral arrangements and event decoration',
      icon: 'Flower',
      slug: 'event-decor'
    },
    {
      id: 'mandap-design',
      title: 'Mandap / Stage Design',
      description: 'Traditional mandap and stage design services',
      icon: 'Home',
      slug: 'mandap-design'
    },
    {
      id: 'led-screens',
      title: 'LED Screens / Visual FX',
      description: 'Modern audio-visual and lighting solutions',
      icon: 'Monitor',
      slug: 'led-screens'
    },
    {
      id: 'furniture-rentals',
      title: 'Lounge Furniture / Rentals',
      description: 'Elegant furniture and rental services',
      icon: 'Armchair',
      slug: 'furniture-rentals'
    }
  ];

  // Attire & Accessories categories
  const attireCategories = [
    {
      id: 'bridal-outfits',
      title: 'Bridal / Groom Outfits',
      description: 'Traditional wedding attire and custom outfits',
      icon: 'Shirt',
      slug: 'bridal-outfits'
    },
    {
      id: 'jewelry-rental',
      title: 'Jewelry Rental',
      description: 'Exquisite jewelry rental for weddings',
      icon: 'Gem',
      slug: 'jewelry-rental'
    },
    {
      id: 'designers-boutiques',
      title: 'Designers & Boutiques',
      description: 'Custom designers and boutique services',
      icon: 'Palette',
      slug: 'designers-boutiques'
    }
  ];

  // Planning & Logistics categories
  const planningCategories = [
    {
      id: 'wedding-planners',
      title: 'Full-Service Wedding Planners',
      description: 'Complete wedding planning and coordination',
      icon: 'Calendar',
      slug: 'wedding-planners'
    },
    {
      id: 'day-coordinators',
      title: 'Day-of Coordinators',
      description: 'Professional wedding day coordination services',
      icon: 'Clock',
      slug: 'day-coordinators'
    },
    {
      id: 'budget-planning',
      title: 'Budget Planning Experts',
      description: 'Wedding budget planning and financial guidance',
      icon: 'DollarSign',
      slug: 'budget-planning'
    },
    {
      id: 'baraat-rentals',
      title: 'Luxury Car / Horse Baraat Rentals',
      description: 'Traditional and luxury transportation for groom',
      icon: 'Car',
      slug: 'baraat-rentals'
    }
  ];

  // Stationery & Favors categories
  const stationeryCategories = [
    {
      id: 'invitations',
      title: 'Invitations / Cards',
      description: 'Beautiful wedding invitations and cards',
      icon: 'Mail',
      slug: 'invitations'
    },
    {
      id: 'digital-invites',
      title: 'Digital Invites',
      description: 'Modern digital invitation solutions',
      icon: 'Smartphone',
      slug: 'digital-invites'
    },
    {
      id: 'return-gifts',
      title: 'Return Gifts / Favors',
      description: 'Thoughtful wedding favors and return gifts',
      icon: 'Gift',
      slug: 'return-gifts'
    }
  ];

  // Accommodations categories
  const accommodationCategories = [
    {
      id: 'hotel-blocks',
      title: 'Hotel Blocks',
      description: 'Group accommodation booking for wedding guests',
      icon: 'Building2',
      slug: 'hotel-blocks'
    },
    {
      id: 'guest-management',
      title: 'Guest Management Services',
      description: 'Professional guest coordination and management',
      icon: 'Users',
      slug: 'guest-management'
    },
    {
      id: 'hospitality-kits',
      title: 'Hospitality Kits',
      description: 'Welcome kits and guest amenities',
      icon: 'Package',
      slug: 'hospitality-kits'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-50 to-secondary-50 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-primary">
              Find Trusted South Asian Wedding Vendors
            </h1>
            <p className="text-xl md:text-2xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
              Discover culturally aligned professionals for every step of your celebration — from pre-wedding rituals to post-reception memories.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <FilterSection />

      {/* Category Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        <CategorySection
          title="Entertainment"
          icon="Music"
          categories={entertainmentCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Photography & Videography"
          icon="Camera"
          categories={photographyCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Beauty & Grooming"
          icon="Palette"
          categories={beautyCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Cultural & Ritual Services"
          icon="Crown"
          categories={culturalCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Venues & Catering"
          icon="Building"
          categories={venueCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Decor & Production"
          icon="Flower"
          categories={decorCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Attire & Accessories"
          icon="Shirt"
          categories={attireCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Planning & Logistics"
          icon="Calendar"
          categories={planningCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Stationery & Favors"
          icon="Mail"
          categories={stationeryCategories}
          onCategoryClick={handleCategoryClick}
        />

        <CategorySection
          title="Accommodations"
          icon="Building2"
          categories={accommodationCategories}
          onCategoryClick={handleCategoryClick}
        />
      </div>

      {/* Cultural Context Section */}
      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="font-heading font-bold text-3xl text-text-primary">
              Understanding South Asian Wedding Traditions
            </h2>
            <p className="text-lg text-text-secondary leading-relaxed">
              Each vendor type plays a crucial role in creating authentic South Asian wedding celebrations.
              From the sacred Mandap setup to the joyous Dhol beats during Baraat, every element carries
              deep cultural significance that connects generations and honors traditions.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <div className="w-6 h-6 text-primary">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Cultural Authenticity
                </h3>
                <p className="text-text-secondary font-caption">
                  Our vendors understand the nuances of different South Asian traditions and regional customs.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="w-12 h-12 bg-secondary-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <div className="w-6 h-6 text-secondary">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4h2v-7.5c0-1.1.9-2 2-2h2V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V9h2c1.1 0 2 .9 2 2v7h-2v-4h-3v4h-2v-7.5c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2V18H4z" />
                    </svg>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Family Involvement
                </h3>
                <p className="text-text-secondary font-caption">
                  We help coordinate with extended families to ensure everyone's traditions are honored.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="w-12 h-12 bg-accent-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <div className="w-6 h-6 text-accent">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Modern Fusion
                </h3>
                <p className="text-text-secondary font-caption">
                  Perfect blend of traditional elements with contemporary American wedding styles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BrowseCategoriesAllVendorTypes;